import type { ApiOperation } from './generated'
import { AbortedError, ApiError, NetworkError } from './errors'

/**
 * The transport half of the API client.
 *
 * The generated half (`./generated`) is data: where each operation lives and what it carries.
 * This half is behaviour: headers, query serialisation, one-shot refresh on `401`, and the
 * re-auth header for destructive work. Keeping the split means the transport can grow without a
 * single line of it ending up in a file that the next `npm run api:generate` overwrites.
 *
 * There is no privileged channel here and there never may be (design §8.2). Everything the
 * dashboard can do, it does through operations declared in the specification.
 */

type Primitive = string | number | boolean

/**
 * The header that carries a single-use re-authentication ticket.
 *
 * **`X-Reauthentication`.** The server is authoritative and this is what it reads —
 * `core/src/Api/State/Processor/OperationRequestProcessor.php` and
 * `core/src/Api/Security/Reauthentication/ReauthenticationGuard.php`. The name is exported rather
 * than spelled at each call site because the previous client used `X-OuiObserve-Reauth`, which no
 * code on the server has ever looked for: every destructive operation it sent arrived without a
 * ticket and was refused.
 *
 * The same ticket may instead be sent as `reauthentication_ticket` in the operation body. The
 * header is used here so that the ticket does not end up in a body that gets logged or replayed.
 */
export const REAUTH_HEADER = 'X-Reauthentication'

/**
 * What the client will accept back.
 *
 * JSON-LD first: it is the only representation in which a collection carries `totalItems`, and a
 * dashboard that paginates needs it (see `./collections.ts`). Plain JSON is kept as a fallback
 * with a lower `q` for the endpoints that offer nothing else — `POST /api/v1/auth/token` is
 * answered by the security firewall rather than by API Platform and speaks only `application/json`.
 * `application/problem+json` is listed because errors come back in that type whatever was asked
 * for (`core/src/Api/Error/ApiProblemSubscriber.php`).
 */
const ACCEPT = 'application/ld+json, application/json;q=0.9, application/problem+json;q=0.9'

export interface RequestOptions<TPath, TQuery, TBody, THeaders> {
  path?: TPath
  query?: TQuery
  body?: TBody
  headers?: THeaders & Record<string, string | undefined>
  signal?: AbortSignal
  /** Skip the refresh-and-retry dance. Used by the refresh call itself, to avoid recursion. */
  noRefresh?: boolean
}

export interface ClientHooks {
  /** Current access token, or null when there is no session. */
  getAccessToken: () => string | null
  /**
   * Obtain a new access token after a `401`. Resolve with the token, or null to give up.
   *
   * The client calls this at most once per request and de-duplicates concurrent callers, so a
   * screen firing six requests against an expired token performs one refresh, not six.
   */
  refresh?: () => Promise<string | null>
  /** Called once when refresh fails, so the app can drop to the login screen. */
  onSessionExpired?: () => void
  /** Called for every completed request. Wired to the dev-tools request log. */
  onRequest?: (record: RequestRecord) => void
}

export interface RequestRecord {
  operationId: string
  method: string
  url: string
  status: number | null
  durationMs: number
  error: unknown
}

export interface ClientOptions extends ClientHooks {
  baseUrl?: string
  fetch?: typeof globalThis.fetch
  /** Wall-clock budget for one request. A hung request must not hold a spinner forever. */
  timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 30_000

/**
 * Serialise one query value.
 *
 * Arrays repeat the key (`?kind=site&kind=server`) rather than joining with commas: the
 * specification declares them as `type: array`, repeated keys are what the core parses, and a
 * comma-joined value would quietly become a single filter containing a comma.
 *
 * `undefined` and `null` are dropped. `null` is not "unknown" here — in a query string there is
 * no way to say "unknown", so an absent parameter is the only honest encoding.
 */
const appendQuery = (params: URLSearchParams, key: string, value: unknown): void => {
  if (value === undefined || value === null) return
  if (Array.isArray(value)) {
    for (const entry of value) appendQuery(params, key, entry)
    return
  }
  if (value === '') return
  params.append(key, String(value as Primitive))
}

export const buildQueryString = (
  query: Record<string, unknown> | undefined,
  allowed: readonly string[],
): string => {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const key of allowed) appendQuery(params, key, query[key])
  const rendered = params.toString()
  return rendered === '' ? '' : `?${rendered}`
}

export const buildPath = (
  template: string,
  names: readonly string[],
  values: Record<string, unknown> | undefined,
): string => {
  let path = template
  for (const name of names) {
    const value = values?.[name]
    if (value === undefined || value === null || value === '') {
      throw new Error(`Missing path parameter "${name}" for "${template}".`)
    }
    path = path.replace(`{${name}}`, encodeURIComponent(String(value)))
  }
  return path
}

export class ApiClient {
  private readonly baseUrl: string
  private readonly doFetch: typeof globalThis.fetch
  private readonly timeoutMs: number
  private readonly hooks: ClientHooks
  /** In-flight refresh, shared by every request that hit a 401 while it was running. */
  private refreshing: Promise<string | null> | null = null

  constructor(options: ClientOptions) {
    // Empty by default — i.e. same origin, no prefix. The generated operation paths are the
    // server's own and already carry the whole path, `/api/v1/assets` and not `/assets`. The
    // previous default of `/api/v1` was a second copy of the prefix and is exactly the kind of
    // thing the hand-written specification made possible: every request went to
    // `/api/v1/api/v1/…`. `baseUrl` remains configurable because deploying the dashboard on its
    // own host is a deployment decision, but it is now an *origin*, not a path prefix.
    this.baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '')
    this.doFetch = options.fetch ?? globalThis.fetch.bind(globalThis)
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
    this.hooks = options
  }

  /**
   * Send one operation.
   *
   * On `401` with a `refresh` hook available, refreshes once and replays the request. A second
   * `401` ends the session rather than looping: an authentication retry loop against a core that
   * has revoked the account is a self-inflicted denial of service.
   */
  async call<TPath, TQuery, TBody, TResponse, THeaders>(
    operation: ApiOperation<TPath, TQuery, TBody, TResponse, THeaders>,
    options: RequestOptions<TPath, TQuery, TBody, THeaders> = {},
  ): Promise<TResponse> {
    const started = Date.now()
    const path = buildPath(
      operation.path,
      operation.pathParams,
      options.path as Record<string, unknown> | undefined,
    )
    const query = buildQueryString(
      options.query as Record<string, unknown> | undefined,
      operation.queryParams,
    )
    const url = `${this.baseUrl}${path}${query}`

    let status: number | null = null
    let failure: unknown = null

    try {
      let response = await this.send(operation, url, options)
      status = response.status

      if (response.status === 401 && operation.secured && !options.noRefresh) {
        const token = await this.refreshOnce()
        if (token === null) {
          this.hooks.onSessionExpired?.()
        } else {
          response = await this.send(operation, url, options)
          status = response.status
        }
      }

      return await this.interpret<TResponse>(operation, response)
    } catch (error) {
      failure = error
      throw error
    } finally {
      this.hooks.onRequest?.({
        operationId: operation.operationId,
        method: operation.method,
        url,
        status,
        durationMs: Date.now() - started,
        error: failure,
      })
    }
  }

  private async send(
    operation: ApiOperation<unknown, unknown, unknown, unknown, unknown>,
    url: string,
    options: RequestOptions<unknown, unknown, unknown, unknown>,
  ): Promise<Response> {
    const headers: Record<string, string> = { Accept: ACCEPT }

    if (operation.hasBody && options.body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    if (operation.secured) {
      const token = this.hooks.getAccessToken()
      if (token) headers.Authorization = `Bearer ${token}`
    }

    for (const [name, value] of Object.entries(
      (options.headers ?? {}) as Record<string, string | undefined>,
    )) {
      if (value !== undefined) headers[name] = value
    }

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), this.timeoutMs)
    const onOuterAbort = () => controller.abort()
    options.signal?.addEventListener('abort', onOuterAbort, { once: true })

    try {
      return await this.doFetch(url, {
        method: operation.method,
        headers,
        body:
          operation.hasBody && options.body !== undefined
            ? JSON.stringify(options.body)
            : undefined,
        signal: controller.signal,
        credentials: 'omit',
      })
    } catch (error) {
      if (options.signal?.aborted) throw new AbortedError(operation.operationId)
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new AbortedError(operation.operationId)
      }
      throw new NetworkError(operation.operationId, error)
    } finally {
      clearTimeout(timer)
      options.signal?.removeEventListener('abort', onOuterAbort)
    }
  }

  private async interpret<TResponse>(
    operation: ApiOperation<unknown, unknown, unknown, unknown, unknown>,
    response: Response,
  ): Promise<TResponse> {
    const requestId = response.headers.get('X-Request-Id')

    if (response.status === 204 || operation.emptyResponse) {
      if (response.ok) return undefined as TResponse
    }

    let body: unknown = null
    const text = await response.text().catch(() => '')
    if (text !== '') {
      try {
        body = JSON.parse(text)
      } catch {
        body = null
      }
    }

    if (!response.ok) {
      throw ApiError.fromResponse(response.status, body, requestId, operation.operationId)
    }

    return body as TResponse
  }

  /** De-duplicated refresh. Every caller that arrives while one is running awaits the same one. */
  private refreshOnce(): Promise<string | null> {
    if (!this.hooks.refresh) return Promise.resolve(null)
    if (this.refreshing) return this.refreshing

    this.refreshing = this.hooks
      .refresh()
      .catch(() => null)
      .finally(() => {
        this.refreshing = null
      })

    return this.refreshing
  }
}

/**
 * The client the application uses.
 *
 * Created in `main.ts` with hooks that read the auth store, and injected so that tests can pass a
 * different one. Nothing imports a module-level singleton: a singleton client is a singleton
 * session, and the tests would then share one.
 */
export const createApiClient = (options: ClientOptions): ApiClient => new ApiClient(options)
